/**
 * IMPORTS
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDays, IdCard, Lock, Package, User2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// components
import { Button } from "@components/button/button";
import { MaskedInputCpfCnpj } from "@components/masked-input-cpf-cnpj";
import { MaskedInputPhone } from "@components/masked-input-phone";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

// schema
import { clienteSchema } from "@pages/admin/editar-dados-cliente/dados-cliente/schema";
import { DeliveryOptions } from "@pages/admin/gerenciar-clientes/components/opcao-entrega";

import { useBuscarTiposEntrega } from "@/hooks/tipos-entregas/buscar-tipos-entrega";
import { useAtualizarUsuario } from "@/hooks/usuários/atualizar-cliente";
import { useBuscarPorUsuario } from "@/hooks/usuários/buscar-por-um-cliente";

import { useStoreZustandTiposEntregas } from "@/store-zustand/tipos-entrega";

import { Spinner } from "@/common/icons/spinner";
import { formatarDocumento, limparDocumento } from "@/utils/format-document";
import { formatarTelefone, limparTelefone } from "@/utils/format-telefone";
import { sleep } from "@/utils/sleep/sleep";

const DadosCliente = () => {
  const location = useLocation();
  const { cliente } = location.state || {};

  const { user, isLoading } = useBuscarPorUsuario(cliente?.id);
  const { tiposEntrega } = useBuscarTiposEntrega();

  const { atualizarUsuarioAsync } = useAtualizarUsuario();

  const { tipoEntregaId, setTipoEntregaId } = useStoreZustandTiposEntregas();

  const [isCreate, setIsCreate] = useState(false);

  // API Nest devolve createdAt; o Laravel devolvia created_at
  const dataCriacaoIso = user?.createdAt ?? user?.created_at;
  const dataCriacao = dataCriacaoIso ? new Date(dataCriacaoIso).toLocaleString("pt-BR") : "N/A";

  type FormData = z.infer<typeof clienteSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(clienteSchema) });

  const onSubmit = useCallback(
    async (data: FormData) => {
      setIsCreate(true);

      const tipoEntrega = tiposEntrega?.find((tipo) => tipo?.id === tipoEntregaId?.id);

      const payload = {
        nome: data.nome_identificacao,
        email: data.email,
        documento: limparDocumento(data.documento),
        ativo: true,
        telefone: limparTelefone(data.telefone_contato),
        tipo_entrega_id: tipoEntrega?.id,
      } as any;

      await atualizarUsuarioAsync({
        user_id: cliente?.id,
        payload: payload,
      });

      setValue("nome_identificacao", data.nome_identificacao);
      setValue("email", data.email);
      setValue("documento", formatarDocumento(data.documento)!);
      setValue("telefone_contato", formatarTelefone(data.telefone_contato)!);

      console.log("tipo entega:", tipoEntrega);

      setIsCreate(false);
      await sleep(1000);
      // window.location.reload();
    },
    [atualizarUsuarioAsync, cliente, setValue, tipoEntregaId, tiposEntrega]
  );

  useEffect(() => {
    if (user !== null) {
      setValue("nome_identificacao", user?.nome);
      setValue("email", user?.email);
      setValue("documento", formatarDocumento(user?.documento)!);
      setValue("telefone_contato", formatarTelefone(user?.telefone)!);
      return;
    }
  }, [user, setValue]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 rounded-xl bg-white p-8 shadow">
      <div className="flex items-center">
        <User2 className="text-2xl font-bold text-zinc-900" />
        <h2 className="ml-2 border-b text-2xl font-bold text-zinc-900">
          Atualizar informações do cliente
        </h2>
      </div>

      {isLoading ? (
        <div className="flex h-96 w-full flex-col items-center justify-center space-x-3">
          <Spinner />
          <p className="text-zinc-400">Carregando dados...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700">Nome da Empresa</label>
                <div className="flex h-12 items-center gap-2 rounded-lg border bg-zinc-50 px-4">
                  <IdCard className="text-zinc-400" />
                  <input
                    {...register("nome_identificacao")}
                    placeholder="Informe o nome da Empresa"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.nome_identificacao && (
                  <p className="text-sm text-red-500">{errors.nome_identificacao.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700">CNPJ/CPF</label>
                <div className="flex h-12 items-center gap-2 rounded-lg border bg-zinc-50 px-4">
                  <IdCard className="text-zinc-400" />

                  <MaskedInputCpfCnpj
                    {...register("documento")}
                    placeholder="Digite CPF ou CNPJ"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors?.documento && (
                  <p className="text-sm text-red-500">{errors.documento.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700">E-mail</label>
                <div className="flex h-12 items-center gap-2 rounded-lg border bg-zinc-50 px-4">
                  <IdCard className="text-zinc-400" />
                  <input
                    {...register("email")}
                    placeholder="Informe o e-mail"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700">Telefone do Responsável</label>
                <div className="flex h-12 items-center gap-2 rounded-lg border bg-zinc-50 px-4">
                  <IdCard className="text-zinc-400" />

                  <MaskedInputPhone
                    {...register("telefone_contato")}
                    placeholder="Informe o telefone"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.telefone_contato && (
                  <p className="text-sm text-red-500">{errors.telefone_contato.message}</p>
                )}
              </div>

              {/* somente leitura: fora do form, não vai no payload de atualização */}
              <div className="space-y-2">
                <label htmlFor="data-criacao-cliente" className="text-sm font-medium text-zinc-700">
                  Data de criação
                </label>
                <div
                  className="flex h-12 cursor-not-allowed items-center gap-2 rounded-lg border bg-zinc-100 px-4"
                  title="A data de criação não pode ser editada"
                >
                  <CalendarDays className="text-zinc-400" />
                  <input
                    id="data-criacao-cliente"
                    value={dataCriacao}
                    disabled
                    readOnly
                    className="flex-1 cursor-not-allowed bg-transparent text-lg text-zinc-500 outline-none"
                  />
                  <Lock className="size-4 text-zinc-400" />
                </div>
              </div>
            </div>

            <div className="h-fit rounded-lg bg-zinc-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Package className="text-zinc-800" />
                <h3 className="text-lg font-semibold text-zinc-800">Tipo de Entrega</h3>
              </div>
              <DeliveryOptions
                tiposEntrega={tiposEntrega}
                tipoEntregaSelecionada={user?.tipo_entrega?.tipo || "outros"} // ex: "motoboy_letscom"
                onTipoEntregaChange={(novoTipo) => {
                  setTipoEntregaId(novoTipo as any);
                  // Atualize seu form ou envie pro backend
                }}
              />
            </div>
          </div>

          <Button disabled={isCreate} type="submit" className="mt-6 w-full ring-offset-background">
            {isCreate ? <SpinnerSimple /> : <>Alterar informações</>}
          </Button>
        </form>
      )}
    </div>
  );
};

export { DadosCliente };
